import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import styled, {keyframes} from 'styled-components';
import {useAppSelector} from 'store/customer';
import {
    getHistoryStack,
    getIsBusy,
    getIsEditHeightView,
    getIsTopView,
} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {Icon} from 'shared/helpers';
import {mobile} from 'shared/Breakpoints';
import {useRoomPlannerContext} from 'components/customer/RoomPlanner/components/RoomPlannerContext';
import {useTutorialHelper} from 'shared/components/Header/helper/useTutorialHelper';
import {
    canRedo as getCanRedo,
    canUndo as getCanUndo,
} from 'components/customer/RoomPlanner/components/helpers/UndoRedo';

const renderWithPendingLoader = (
    text: string,
    keyPrefix: string
): React.ReactNode => {
    const parts = text.split('...');
    if (parts.length === 1) return text;
    // Use the character offset of each chunk as the key so we never rely on
    // the array index (satisfies the react/no-array-index-key rule).
    let charOffset = 0;
    return parts.map((chunk, idx) => {
        const key = `${keyPrefix}-${charOffset}`;
        charOffset += chunk.length + 3; // 3 === '...'.length
        return (
            <React.Fragment key={key}>
                {chunk}
                {idx < parts.length - 1 && (
                    <PendingNumberSkeleton aria-hidden="true" />
                )}
            </React.Fragment>
        );
    });
};

const UndoRedoComponent = () => {
    const historyStack = useAppSelector(getHistoryStack);
    const isBusy = useAppSelector(getIsBusy);
    const isTopView = useAppSelector(getIsTopView);
    const isEditHeightView = useAppSelector(getIsEditHeightView);

    const {undoHandler, redoHandler, goToHistoryIndex} =
        useRoomPlannerContext();

    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const activeItemRef = useRef<HTMLLIElement | null>(null);

    const {showUndoRedoTutorial, hideUndoRedoTutorial} = useTutorialHelper();

    // Current active history index for list highlight and go-to
    const currentActiveIndex = useMemo(() => {
        if (!historyStack?.length) return -1;
        for (let i = historyStack.length - 1; i >= 0; i--) {
            if (historyStack[Number(i)].isActive) return i;
        }
        return -1;
    }, [historyStack]);

    // Block undo/redo and history clicks while any history item is executing (e.g. save in progress)
    const isActiveExecuting = useMemo(() => {
        return Boolean(isBusy);
    }, [isBusy]);

    const undo = useCallback(() => {
        hideUndoRedoTutorial();
        undoHandler?.();
    }, [undoHandler]);

    const redo = useCallback(() => {
        hideUndoRedoTutorial();
        redoHandler?.();
    }, [redoHandler]);

    const toggleHistoryOpen = useCallback(() => {
        hideUndoRedoTutorial();
        setIsHistoryOpen((o) => !o);
    }, []);

    const handleHistoryItemClick = useCallback(
        (e: React.MouseEvent<HTMLElement>) => {
            if (isActiveExecuting) return;
            const index = e.currentTarget.dataset.index;
            const numberIndex = Number(index);

            if (index != null) goToHistoryIndex?.(numberIndex);
        },
        [goToHistoryIndex, isActiveExecuting]
    );

    // Calculate if undo is available
    const canUndo = useMemo(() => getCanUndo(historyStack), [historyStack]);

    // Calculate if redo is available
    const canRedo = useMemo(() => getCanRedo(historyStack), [historyStack]);

    // Scroll to active item when history list is opened
    useEffect(() => {
        if (isHistoryOpen && currentActiveIndex >= 0 && activeItemRef.current) {
            activeItemRef.current.scrollIntoView({
                block: 'nearest',
                behavior: 'smooth',
            });
        }
    }, [isHistoryOpen, currentActiveIndex]);

    // Close history on Escape
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isHistoryOpen) {
                setIsHistoryOpen(false);
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isHistoryOpen]);

    useEffect(() => {
        if (historyStack?.length > 0) {
            setTimeout(() => {
                showUndoRedoTutorial('undo-redo-container');
            }, 500);
        }
    }, [historyStack, showUndoRedoTutorial]);

    if (!historyStack?.length || isTopView || isEditHeightView) {
        return null;
    }

    return (
        <UndoRedoContainer id="undo-redo-container">
            <ButtonsRow>
                <ActionButton
                    className="undo-redo-action"
                    type="button"
                    title="Undo"
                    onClick={undo}
                    disabled={!canUndo || isActiveExecuting}>
                    <ActionIcon iconName="Undo.svg" />
                    <span className="undo-redo-label">Undo</span>
                </ActionButton>
                <ActionButton
                    className="undo-redo-action"
                    type="button"
                    title="Redo"
                    onClick={redo}
                    disabled={!canRedo || isActiveExecuting}>
                    <ActionIcon iconName="Redo.svg" />
                    <span className="undo-redo-label">Redo</span>
                </ActionButton>
                <HistoryDropdownWrap>
                    <HistoryToggleButton
                        type="button"
                        onClick={toggleHistoryOpen}
                        aria-expanded={isHistoryOpen}>
                        History
                        <ChevronIcon $open={isHistoryOpen} />
                    </HistoryToggleButton>
                    {isHistoryOpen ? (
                        <HistoryList
                            style={{
                                pointerEvents: isActiveExecuting
                                    ? 'none'
                                    : undefined,
                                opacity: isActiveExecuting ? 0.7 : 1,
                            }}>
                            {historyStack.map((item, index) => {
                                const title = item.title
                                    ? `${index + 1}. ${item.title}`
                                    : item.action;
                                return (
                                    <HistoryItem
                                        key={`${title}-${item.timestamp.toString()}`}
                                        data-index={index}
                                        ref={
                                            index === currentActiveIndex
                                                ? activeItemRef
                                                : undefined
                                        }
                                        $active={index === currentActiveIndex}
                                        onClick={handleHistoryItemClick}>
                                        <HistoryItemTitle title={title}>
                                            {title
                                                // First handle bold segments wrapped in ** **
                                                .split(/\*\*(.*?)\*\*/g)
                                                .map((part, i) =>
                                                    i % 2 === 1 ? (
                                                        <strong
                                                            key={`i-${part}`}>
                                                            {part}
                                                        </strong>
                                                    ) : (
                                                        // Then handle italic segments wrapped in * *
                                                        part
                                                            .split(/\*(.*?)\*/g)
                                                            .map(
                                                                (subPart, j) =>
                                                                    j % 2 ===
                                                                    1 ? (
                                                                        <em
                                                                            key={`j-${subPart}`}>
                                                                            {
                                                                                subPart
                                                                            }
                                                                        </em>
                                                                    ) : (
                                                                        renderWithPendingLoader(
                                                                            subPart,
                                                                            `${i}-${j}`
                                                                        )
                                                                    )
                                                            )
                                                    )
                                                )}
                                        </HistoryItemTitle>
                                        <HistoryItemRevertHint
                                            style={{
                                                display:
                                                    index === currentActiveIndex
                                                        ? 'none'
                                                        : undefined,
                                            }}>
                                            Revert to this step
                                        </HistoryItemRevertHint>
                                    </HistoryItem>
                                );
                            })}
                        </HistoryList>
                    ) : null}
                </HistoryDropdownWrap>
            </ButtonsRow>
        </UndoRedoContainer>
    );
};

const UndoRedoContainer = styled.div`
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    pointer-events: none;

    > * {
        pointer-events: auto;
    }
`;

const ButtonsRow = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: center;
`;

const HistoryDropdownWrap = styled.div`
    position: relative;
    display: flex;
    align-items: center;

    /* Phone: block flow so the fixed menu's natural (static) position is the
       line under the button. No measuring needed for its top. */
    ${mobile`
        display: block;
    `}
`;

const HistoryToggleButton = styled.button`
    display: inline-flex;

    ${mobile`
        display: flex;
    `}
    align-items: center;
    gap: 6px;
    border: 1px solid #e6e8ec;
    border-radius: 10px;
    padding: 6px 10px;
    background: #fff;
    color: #374151;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
`;

const ChevronIcon = styled.span<{$open: boolean}>`
    display: inline-block;
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 5px solid #555;
    transform: ${({$open}) => ($open ? 'rotate(180deg)' : 'none')};
    transition: transform 0.2s ease;
`;

const HistoryList = styled.ul`
    position: absolute;
    top: calc(100% + 6px);
    left: 50%;
    transform: translateX(-50%);
    z-index: 6;
    margin: 0;
    padding: 6px 0;
    width: min(400px, 70vw);
    max-height: 240px;
    overflow-y: auto;
    list-style: none;
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);

    /* Phone: centre on the screen so the action text is not cut off by the
       right edge. top:auto keeps it just under the button; left is measured
       against the viewport. Desktop stays centred on the History button. */
    ${mobile`
        position: fixed;
        top: auto;
        margin-top: 6px;
        left: 50%;
        transform: translateX(-50%);
        width: min(400px, calc(100vw - 24px));
        z-index: 20;
    `}
`;

const HistoryItemRevertHint = styled.span`
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 11px;
    color: #666;
    font-weight: 400;
    opacity: 0;
    transition: opacity 0.15s ease;
    background: #fff;
    padding: 2px 6px;
    border-radius: 4px;
`;

const HistoryItem = styled.li<{$active?: boolean}>`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 12px;
    font-size: 12px;
    color: #333;
    border-bottom: 1px solid #eee;
    cursor: pointer;
    background: ${({$active}) => ($active ? '#e3f2fd' : 'transparent')};
    font-weight: ${({$active}) => ($active ? 600 : 400)};

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background: ${({$active}) => ($active ? '#bbdefb' : '#f5f5f5')};
    }

    &:hover ${HistoryItemRevertHint} {
        opacity: 1;
    }

    ${mobile`
        &:hover ${HistoryItemRevertHint} {
            opacity: 0;
        }
    `}
`;

const HistoryItemTitle = styled.span`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    ${mobile`
        overflow: visible;
        text-overflow: unset;
        white-space: normal;
    `}
`;

const ActionButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid #e6e8ec;
    border-radius: 10px;
    padding: 6px 10px;
    background: #fff;
    color: #374151;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;

    &:disabled {
        cursor: default;
        opacity: 0.55;
    }
`;

const ActionIcon = styled(Icon)`
    width: 16px;
    height: 16px;
`;

const shimmer = keyframes`
    0%   { background-position: -80px 0; }
    100% { background-position:  80px 0; }
`;

// Sized to match roughly two digits at 12 px — same em-width as "12" in the
// surrounding font so the bracket "[1-▓▓]" stays visually balanced.
const PendingNumberSkeleton = styled.span`
    display: inline-block;
    width: 1.6ch;
    height: 0.85em;
    border-radius: 2px;
    vertical-align: middle;
    margin-bottom: 1px;
    background: linear-gradient(90deg, #d0d0d0 25%, #e8e8e8 50%, #d0d0d0 75%);
    background-size: 160px 100%;
    animation: ${shimmer} 1.2s linear infinite;
`;

export default UndoRedoComponent;
